import * as angular from 'angular';
import datacatalog from '../../integration/datacatalog/datacatalog-services';
import server from '../../integration/server/server-services';
import FeatureList from '../../model/model-featurelist';
import FileList, {LocalFile} from '../../model/model-filelist';
import {AUTH_EVENTS} from '../security/security-module';
import UpdateDatacatalogService from '../../integration/datacatalog/UpdateDatacatalogService';
import {
    ArchivedState,
    Contract, ContractLock, ContractMilestone,
    ContractObjectListItem,
    ContractObjectLists, ContractStatus, ContractType,
    Contributor, Destination, MapComment, Comment
} from "../../integration/representation/contract";
import {CommentIndex, Feature, FeaturesResult, Operation} from "../../integration/representation/feature";
import messagesService from "../messages/messages-service";
import {ContractFile, SosiFileLine} from "../../integration/representation/file";
import {IHttpPromise, IHttpResponse, IPromise, IQResolveReject} from "angular";
import {ValidationIssue} from "../../integration/representation/validation-issue";
import {Math as threeMath} from 'three';
import generateUUID = threeMath.generateUUID;

interface NewComment {
    comment: string;
    error: boolean;
    destination?: Destination;
}

/**
 * Simplified feature type used for both NVDB and FKB feature types in contract.
 */
export interface ContractFeatureType {
    id: number,
    name: string,
    sortNum: number,
    destination: "NVDB" | "FKB"
}

interface FeatureTypeComment {
    featureTypeId: number;
    comment: string;
    destination: Destination;
    username?: string;
    error: boolean;
}

interface FeatureComment {
    featureId: string;
    comment: string;
    destination: Destination;
    username?: string;
    error: boolean;
}

interface ContractFileWithContent extends ContractFile {
    content: SosiFileLine[];
}


export default class contractService {
    public loading = {features: false, nvdbfeatures: false, fkbfeatures: false, files: false, contract: false};

    // Attrs
    public contractId?: string = undefined;
    public featureTypes?: { [key: number]: ContractFeatureType }
    public fkbFeatureTypes?: { [key: number]: ContractFeatureType }
    public featureList: FeatureList = undefined;
    public fkbFeatureList: FeatureList = undefined;

    public fileList = new FileList();
    public objectList: ContractObjectListItem[] = [];
    public fkbObjectList: ContractObjectListItem[] = [];

    public selectedFileDestination: Destination = 'NVDB';
    public uploadingFiles = 0;
    public featureTypeComments: CommentIndex  = undefined;
    public fkbFeatureTypeComments: CommentIndex = undefined;
    public nvdbFileList = new FileList();
    public fkbFileList = new FileList();

    /*
     isFetchingData and promisesToResolve is used to handle calls to selectContract.
     If two calls are done where the second is performed before the first is resolved we need to wait
     with resolving the second untill the first is resolved.
     */
    public isFetchingData = false;
    private promisesToResolve: IQResolveReject<contractService>[] = [];

    public hasNvdbFeatures = false;
    public hasAnyFeatures = false;
    public referenceId: string;
    public name: string;
    public assignee: string;
    public assigneeDisplayName: string;
    public contractGroup: string;
    public contractor: string;
    public contributors: Contributor[];
    public county: string;
    public municipality: string;
    public roadCategory: string;
    public roadStatus: string;
    public roadNumber: number;
    public archivedState: ArchivedState;
    public status: ContractStatus;
    public comment: string;
    public comments: Comment[];
    public constructionManager: string;
    public showMessage: messagesService = null;
    public contractType: ContractType = null;
    public completionDate: Date = null;
    public sensitiveCategories: string[];

    constructor(private readonly $rootScope: angular.IRootScopeService,
                private readonly $http: angular.IHttpService,
                private readonly $q: angular.IQService,
                private readonly $timeout: angular.ITimeoutService,
                private readonly $log: angular.ILogService,
                private readonly server: server,
                private readonly datacatalog: datacatalog,
                private readonly $routeParams: angular.route.IRouteParamsService,
                private readonly messages: messagesService,
                private readonly uibDateParser: any, // No typings for Angular Bootstrap
                private readonly updateDatacatalogService: UpdateDatacatalogService) {

        this.showMessage = messages;

        $rootScope.$on(AUTH_EVENTS.AUTH_LOGGED_IN, () => {
            if ($routeParams.contractId) {
                this.contractId = $routeParams.contractId;
                this.selectContract($routeParams.contractId);
            }
        });

        $rootScope.$on(AUTH_EVENTS.AUTH_LOGGED_OUT, () => {
            if (this.contractId !== undefined) {
                this.clear();
            }
        });

        $rootScope.$on('$routeChangeStart', (event, next, prev) => {
            const previousContractId = prev ? prev.params.contractId : undefined;
            const nextContractId = next.params.contractId;
            if (!nextContractId) {
                this.clear();
            } else if (nextContractId && nextContractId !== previousContractId) {
                this.contractId = $routeParams.contractId;
                this.selectContract(nextContractId, (window as any).noLogin);
            }
        });

    }

    setContractBoundary(boundaryWkt: string): void {
        this.server.setContractBoundary(this.contractId, boundaryWkt);
    }

    getContractBoundary() {
        return this.server.getContractBoundary(this.contractId);
    }

    persistObjectLists() {
        const objectLists = {
            nvdb: this.objectList,
            fkb: this.fkbObjectList
        };
        this.server.saveObjectLists(this.contractId, objectLists);
    }

    resolveIssuesAsNotApplicable(issueId: string, resolveSimilar: boolean): IPromise<string[]> {
        const response = this.server.resolveIssuesAsNotApplicable(this.contractId, issueId, resolveSimilar);
        this.featureList.updateIssueCount();
        return response;
    }

    clear() {
        this.contractId = undefined;
        this.featureList = undefined;
        this.fkbFeatureList = undefined;
        this.featureTypeComments = undefined;
        this.selectedFileDestination = 'NVDB';
        this.fkbFeatureTypeComments = undefined;
        this.nvdbFileList = new FileList();
        this.fileList = this.nvdbFileList;
        this.fkbFileList = new FileList();
        this.uploadingFiles = 0;
    }

    readObjectLists() {
        return this.server.readObjectLists(this.contractId).then(function (data: ContractObjectLists) {
            this.objectList = data.nvdb;
            this.fkbObjectList = data.fkb;
        }.bind(this));
    }

    approveLocationals(featureIds: string[]): IPromise<void> {
        return this.server.approveLocationals(this.contractId, featureIds)
    }

    static objectlistItemToTypeId(item: ContractObjectListItem): number {
        return item.typeId;
    }

    hasTypeInObjectList(nvdbId: number, fkbId: number) {
        const nvdbTypes = this.objectList.map(contractService.objectlistItemToTypeId);
        const fkbTypes = this.fkbObjectList.map(contractService.objectlistItemToTypeId);

        return nvdbTypes.indexOf(nvdbId) !== -1 || fkbTypes.indexOf(fkbId) !== -1;
    }

    updateObjectLists(newNvdbObjectlist: ContractObjectListItem[], newFkbObjectlist: ContractObjectListItem[]) {
        this.objectList = newNvdbObjectlist;
        this.fkbObjectList = newFkbObjectlist;
        this.persistObjectLists();
    }

    selectContract(contractId: string, omitLocks = false): IPromise<contractService> {
        const CACHE_EXPIRATION_MS = 7500;

        return this.$q(function (resolve: IQResolveReject<contractService>) {
            const now = new Date().getTime();

            if (this.isFetchingData) {
                this.$log.info('Contract is already being fetched: ' + contractId);
                this.promisesToResolve.push(resolve);
            }
            else if (this.lastFetchTime && (now - this.lastFetchTime) < CACHE_EXPIRATION_MS && this.cachedContract && this.cachedContract.contractId === contractId) {
                this.$log.info('Returning recently fetched contract from cache: ' + contractId);
                resolve(this.cachedContract);
            }
            else {
                this.$log.info('Loading contract ' + contractId);

                this.isFetchingData = true;
                this.loading.contract = true;

                this.promisesToResolve.push(resolve);
                const contract = this;

                this.server.readContract(contractId)
                    .then(function (proj: Contract) {
                        contract.contractId = proj.id;
                        contract.referenceId = proj.referenceId;
                        contract.name = proj.name;
                        contract.assignee = proj.assignee;
                        contract.assigneeDisplayName = proj.assigneeDisplayName;
                        contract.contractGroup = proj.contractGroup;
                        contract.county = '' + proj.county;
                        contract.municipality = proj.municipality;
                        contract.roadCategory = proj.roadCategory;
                        contract.roadStatus = proj.roadStatus;
                        contract.roadNumber = proj.roadNumber;
                        contract.contractor = proj.contractor;
                        contract.contributors = proj.contributors;
                        contract.milestones = proj.milestones.map(milestone => {
                            milestone.deadline = contract.uibDateParser.parse(milestone.deadline, 'yyyy-MM-dd');
                            return milestone;
                        });
                        contract.mapComments = proj.mapComments;
                        contract.objectList = proj.objectList;
                        contract.fkbObjectList = proj.fkbObjectList;
                        contract.comments = proj.comments;
                        contract.archivedState = proj.archivedState;
                        contract.comment = proj.comment;
                        contract.constructionManager = proj.constructionManager;
                        contract.contractType = proj.contractType;
                        contract.completionDate = proj.completionDate ? new Date(proj.completionDate) : null;
                        contract.sensitiveCategories = proj.sensitiveCategories;

                        const lowerCaseStatus = proj.status.toLowerCase();
                        contract.status = lowerCaseStatus.charAt(0).toUpperCase() + lowerCaseStatus.slice(1);

                        contract.loading.contract = false;

                        contract.cachedContract = contract;
                        contract.lastFetchTime = new Date().getTime();

                        const operations = [
                            contract
                        ];
                        if (!omitLocks) {
                            operations.push(contract.readLocks());
                        }

                        return contract.$q.all(operations).then(function () {
                            contract.$log.info('Contract ' + contractId + ' resolving ' + contract.promisesToResolve.length);
                            contract.isFetchingData = false;
                            contract.promisesToResolve.forEach(function (resolve: IQResolveReject<contractService>) {
                                resolve(contract);
                            });
                            contract.promisesToResolve.length = 0;
                        }).catch(function () {
                            console.error("Some operations for features or files failed.");
                        });
                    })
                    .catch(function () {
                        contract.loading.contract = false;
                        contract.isFetchingData = false;
                    });
            }
        }.bind(this));
    }

    resolveFeatureExistance(): void {
        this.hasNvdbFeatures = this.featureList && Object.keys(this.featureList.features).length > 0;
        this.hasAnyFeatures = this.hasNvdbFeatures || Object.keys(this.fkbFeatureList.features).length > 0;
    }

    reloadContract(): IPromise<contractService> {
        const contractId = this.contractId;
        return this.selectContract(contractId);
    }

    updateContract() {
        const proj: Contract = {
            id: this.contractId,
            referenceId: this.referenceId,
            name: this.name,
            assignee: this.assignee,
            contractor: this.contractor,
            contributors: this.contributors,
            county: this.county ? parseInt(this.county) : null,
            municipality: this.municipality,
            roadCategory: this.roadCategory,
            roadStatus: this.roadStatus,
            roadNumber: this.roadNumber,
            objectList: this.objectList,
            fkbObjectList: this.fkbObjectList,
            archivedState: this.archivedState,
            status: this.status.toUpperCase() as ContractStatus,
            comment: this.comment,
            constructionManager: this.constructionManager,
            contractType: this.contractType,
            completionDate: this.completionDate ? this.completionDate.toISOString() : null,
            assigneeDisplayName: null,
            milestones: null,
            mapComments: null,
            lastModified: null,
            comments: null,
            boundaryWkt: null,
            contractGroup: null,
            sensitiveCategories: null
        };
        return this.server.saveContract(proj);
    }

    updateLoadingflag() {
        this.loading.features = this.loading.nvdbfeatures || this.loading.fkbfeatures;
    }


    readFeatureList(): IPromise<FeatureList> {
        this.loading.nvdbfeatures = true;
        this.loading.features = true;
        return this.updateDatacatalogService.updateDatacatalog()
            .then(() => this.server.readFeatures(this.contractId, 'NVDB'))
            .then((data: FeaturesResult) => {
                const features = data.features;
                this.featureTypes = mapNvdbFeatureTypes(features, this.datacatalog);
                this.featureList = new FeatureList(this.datacatalog, features, this, 'NVDB', data.featureTypeIssues);
                this.featureTypeComments = data.featureTypeComments;

                this.loading.nvdbfeatures = false;
                this.updateLoadingflag();
                this.resolveFeatureExistance();
                return this.featureList;
            },
                (rejection: any) => {
                this.loading.nvdbfeatures = false;
                this.updateLoadingflag();
                return rejection;
            })
    }

    readFKBFeatureList() {
        this.loading.fkbfeatures = true;
        this.loading.features = true;
        return this.updateDatacatalogService.updateDatacatalog()
            .then(() => this.server.readFeatures(this.contractId, 'FKB'))
            .then((data) => {
                    const features = data.features;
                    this.fkbFeatureTypes = mapFkbFeatureTypes(features, this.datacatalog);
                    this.fkbFeatureList = new FeatureList(this.datacatalog, features, this, 'FKB', data.featureTypeIssues);
                    this.fkbFeatureTypeComments = data.featureTypeComments;
                    this.loading.fkbfeatures = false;
                    this.updateLoadingflag();
                    this.resolveFeatureExistance();
                    return this.fkbFeatureList;
                },
                (rejection) => {
                    this.loading.fkbfeatures = false;
                    this.updateLoadingflag();
                    return rejection;
                })
    }

    readFileList() {
        this.loading.files = true;
        const nvdbFilesPromise = this.server.readFiles(this.contractId, 'NVDB');
        const fkbFilesPromise = this.server.readFiles(this.contractId, 'FKB');

        return this.$q.all([nvdbFilesPromise, fkbFilesPromise]).then(function (responses: [ContractFile[], ContractFile[]]) {
            this.nvdbFileList = new FileList(responses[0]);
            this.fkbFileList = new FileList(responses[1]);
            this.fileList = (this.selectedFileDestination == 'FKB') ? this.fkbFileList : this.nvdbFileList;
            this.loading.files = false;
        }.bind(this));
    }

    readLocks() {
        this.server.readContractLocks(this.contractId).then(function (data: ContractLock[]) {
            this.locks = data;
        }.bind(this));
    }

    /*
     When uploading lots of files we want to check for updates quickly for the first files,
     then increase timeout for each file.
     As the number of retries increases the likelyhood that the files is done increases.
     */
    static calculateWaitTime(numberInUploadQueue: number, uploadQueueLength: number, pollNumber: number): number {
        const queueFactor = (500 * numberInUploadQueue) / pollNumber;
        const lengthFactor = 75 * uploadQueueLength;
        //console.log('#' + numberInUploadQueue + ' len:' + uploadQueueLength + ' qf=' + queueFactor + ' lf:' + lengthFactor + ' retry: ' + pollNumber + ' timeout: ' + timeout);
        return Math.max(lengthFactor + queueFactor, 1000);
    }

    waitForAcceptedOrRejected(file: ContractFile, pollNumber: number, afterWait?: () => void) {
        if (file.status === 'PROCESSING' || pollNumber === 1) {
            const timeout = contractService.calculateWaitTime(file.numberInUploadQueue ?? 1, this.uploadingFiles, pollNumber);
            this.$timeout(function () {
                this.server.getFile(this.contractId, file.id).then(function (response: ContractFile) {
                    this.waitForAcceptedOrRejected(response, ++pollNumber, afterWait);
                }.bind(this));
            }.bind(this), timeout, false);
        } else {
            // reload file to get validation issues and features.
            this.reloadAfterSubmission(file, afterWait);
        }
    }

    reloadAfterSubmission(file: ContractFile, cb?: (id: string) => void) {
        this.$q.all([
            this.server.getFile(this.contractId, file.id, true),
            this.readFeatureList(),
            this.readFKBFeatureList()
        ]).then(function (responses: [ContractFile, FeaturesResult, any]) {
            const completeFile = responses[0];
            this.fileList.replaceContractFile(file.id, completeFile);
            this.completenessItems = undefined;
            this.decrementNumUploadingFiles();
            this.resolveFeatureExistance();
            this.loading.files = false;
            if(cb){
                cb(completeFile.id);
            }
        }.bind(this));
    }

    decrementNumUploadingFiles() {
        this.uploadingFiles--;
    }

    uploadFile(localFile: File, cb?: (id: string, wantedOperation: Operation | null) => void) {
        if (localFile.size > 10 * 1024 * 1024) {
            this.server.messages.danger('Filen du prøver å laste opp (' + localFile.name + ') er større enn 10MB, som ikke støttes', "For stor");
        } else {
            this.uploadingFiles++;
            const currentlyUploadingFiles = this.uploadingFiles;
            const file = this.fileList.addLocalFile(localFile);
            this.server.uploadFile(this.contractId, this.selectedFileDestination, file)
                .then(function (response: IHttpResponse<ContractFile | undefined>) {
                    file.numberInUploadQueue = currentlyUploadingFiles;
                    this.decrementNumUploadingFiles();
                    if(response.data){
                        this.getFileList().replace(file, response.data);
                        cb(response.data.id, response.data.wantedOperation);
                    } else {
                        this.server.messages.warning(file.name, 'Kunne ikke lese fil');
                    }
                }.bind(this), this.decrementNumUploadingFiles.bind(this), this.getFileList.bind(this))
        }
    }

    getFileList() {
        return this.fileList;
    }

    confirmFileOperation(fileId: string, operation: Operation, cb?: () => void) {
        this.uploadingFiles++;
        this.server.confirmFileOperation(this.contractId, fileId, operation)
            .then((newFile) => {
                this.waitForAcceptedOrRejected(newFile, 1, cb);
            });
    }

    confirmManyFileOperations(fileIds: string[], operation: Operation, cb?: () => void) {
        this.uploadingFiles += fileIds.length;
        this.server.confirmManyFileOperations(this.contractId, fileIds, operation)
            .then((newFiles) => {
                newFiles.forEach((file, index) => {
                    this.waitForAcceptedOrRejected(file, newFiles.length - index, cb);
                });
            });
    }

    deleteFiles(filesToDelete: string[], onDeletion: (fileId: string) => void) {
        const queries: IPromise<void>[] = [];
        filesToDelete.forEach(function (fileId: string) {
            queries.push(this.server.deleteFile(this.contractId, fileId).then(function () {
                onDeletion(fileId);
                this.fileList.removeFile(fileId);
                this.completenessItems = undefined;
            }.bind(this)));
        }.bind(this));
        return this.$q.all(queries).then(this.reloadContract.bind(this));
    }

    readFileContent(file: ContractFile): IPromise<ContractFileWithContent> {
        return this.$q(function (resolve: IQResolveReject<ContractFileWithContent>) {
            if (!(file as ContractFileWithContent).content) {
                return this.server.readFileContentAsLines(this.contractId, file.id).then(function (data: SosiFileLine[]) {
                    (file as ContractFileWithContent).content = data;
                    resolve(file as ContractFileWithContent);
                });
            } else {
                resolve(file as ContractFileWithContent);
            }
        }.bind(this));

    }

    downloadFiles(files: ContractFile[]) {
        this.server.downloadFiles(this.contractId, files);
    }

    addFeatureTypeIssue(featureList: FeatureList, featureTypeComment: Comment, featureTypeId: number, featureTypeName: string) {
        let featureTypeIssues = featureList.featureTypeIssues[featureTypeId];
        if (!featureTypeIssues) {
            featureTypeIssues = [];
            featureList.featureTypeIssues[featureTypeId] = featureTypeIssues;
        }
        featureTypeIssues.push({
            message: featureTypeComment.comment, severity: 'ERROR', code: 'ISSUE_ADDED_BY_OPERATOR',
            location: {
                featureAlias: featureTypeName,
                commentId: featureTypeComment.id,
                contractId: featureTypeComment.contractId
            },
            id: undefined,
            resolution: undefined,
            display: undefined
        });
    }

    getFeatureAndCommentObjects(comment: Comment): {features: FeatureList, comments: CommentIndex} {
        return comment.destination === 'NVDB' ?
            {features: this.featureList, comments: this.featureTypeComments} :
            {features: this.fkbFeatureList, comments:this.fkbFeatureTypeComments};
    }

    getCommentsForFeatureType(comments: CommentIndex, comment: Comment): Comment[] {
        if (!comments[comment.objectId]) {
            comments[comment.objectId] = [];
        }
        return comments[comment.objectId];
    }

    saveFeatureTypeComment(featureTypeComment: FeatureTypeComment): IPromise<Comment> {
        return this.server.saveComment({
            contractId: this.contractId,
            objectId: featureTypeComment.destination + '-' + featureTypeComment.featureTypeId,
            objectType: 'FEATURE_TYPE',
            username: featureTypeComment.username,
            destination: featureTypeComment.destination,
            error: featureTypeComment.error,
            comment: featureTypeComment.comment,
            id: null,
            tempId: generateUUID(),
            createdInstant: null
        }).then(function (savedComment: Comment) {
            const keys = this.getFeatureAndCommentObjects(savedComment);
            const comments = this.getCommentsForFeatureType(keys.comments, savedComment);
            comments.push(savedComment);
            if (savedComment.error) {
                const featureType = featureTypeComment.destination === 'NVDB' ?
                    this.datacatalog.getFeatureType(featureTypeComment.featureTypeId)
                    : this.datacatalog.getFkbFeatureType(featureTypeComment.featureTypeId);
                this.addFeatureTypeIssue(keys.features, savedComment, featureTypeComment.featureTypeId, featureType?.name ?? "Ukjent type");
            }

            return savedComment;
        }.bind(this));
    }

    saveMapComment(mapComment: MapComment): IPromise<MapComment> {
        return this.server.saveContractMapComment(this.contractId, mapComment);
    }

    deleteMapComment(mapComment: MapComment): IHttpPromise<void> {
        return this.server.deleteContractMapComment(this.contractId, mapComment);
    }

    // XXX Return value typed as "any" to ake the method callable
    saveMilestone(milestone: ContractMilestone): IHttpPromise<any> {
        if (milestone.id) {
            return this.server.updateContractMilestone(this.contractId, milestone);
        } else {
            return this.server.createContractMilestone(this.contractId, milestone).then(function (response: IHttpResponse<ContractMilestone>) {
                if (!this.milestones) {
                    this.milestones = [];
                }

                const milestone = response.data;
                this.milestones.push(milestone);
            }.bind(this));
        }
    }

    deleteMilestone(milestoneId: string) {
        return this.server.deleteContractMilestone(this.contractId, milestoneId).then(function () {
            this.milestones = this.milestones.filter(function (m: ContractMilestone) {
                return m.id !== milestoneId;
            });
        }.bind(this));
    }

    deleteContract(contractId?: string) {
        const pId = contractId || this.contractId;
        return this.$q(function (resolve: IQResolveReject<void>, reject: IQResolveReject<any>) {
            this.server.deleteContract(pId).then(function () {
                resolve();
            }, function (error: any) {
                reject(error);
            });
        }.bind(this));
    }

    toggleArchiveContract(contractId: string, shallArchive: string) {
        const id = contractId || this.contractId;
        return this.$q((resolve, reject) => {
            this.server.toggleArchiveContract(id, shallArchive).then(() => {
                resolve();
            }, (error) => {
                reject(error);
            });
        });
    }

    getEvents() {
        return this.server.getContractEvents(this.contractId);
    }

    createContractComment(comment: NewComment, isError: boolean, username?: string) {
        const fullComment: Comment = {
            contractId: this.contractId,
            objectId: this.contractId,
            objectType: 'PROJECT',
            destination: comment.destination || 'NVDB',
            username,
            error: isError,
            comment: comment.comment,
            id: null,
            tempId: generateUUID(),
            createdInstant: null
        };
        return this.server.saveComment(fullComment).then(function (comment: Comment) {
            this.comments.push(comment);
            return comment;
        }.bind(this));
    }

    saveFeatureComment(comment: FeatureComment) {
        const fullComment: Comment = {
            contractId: this.contractId,
            objectId: comment.featureId,
            objectType: 'FEATURE',
            destination: comment.destination || 'NVDB',
            username: comment.username,
            error: comment.error,
            comment: comment.comment,
            id: null,
            tempId: generateUUID(),
            createdInstant: null
        };

        return this.server.saveComment(fullComment).then(function (comment: Comment) {
            const keys = this.getFeatureAndCommentObjects(comment);
            const feature = keys.features.getFeatureById(comment.objectId);
            feature.comments.push(comment);
            if (comment.error) {
                const issue = {
                    message: comment.comment,
                    severity: 'ERROR',
                    code: 'ISSUE_ADDED_BY_OPERATOR',
                    location: {featureAlias: feature.name, commentId: comment.id}
                };
                feature.issues.push(issue);
            }
            return comment;
        }.bind(this));
    }

    deleteComment(comment: Comment) {
        return this.server.deleteComments(this.contractId, [comment.id])
            .then(() => {
                const keys = this.getFeatureAndCommentObjects(comment);
                if (comment.objectType === 'FEATURE_TYPE') {
                    const comments = keys.comments[comment.objectId];
                    const idx = comments.findIndex(c => c.id === comment.id);
                    comments.splice(idx, 1);
                    if (comments.length === 0) {
                        delete keys.comments[comment.objectId];
                    }
                }

                if (comment.error) {
                    const commentIdFilter = function (issue: ValidationIssue): boolean {
                        return issue.location.commentId !== comment.id;
                    };

                    if (comment.objectType === 'FEATURE') {
                        const feature = keys.features.getFeatureById(comment.objectId);
                        feature.issues = feature.issues.filter(commentIdFilter);
                    } else if (comment.objectType === 'FEATURE_TYPE') {
                        const featureTypeId = parseInt(comment.objectId.split('-')[1]);
                        const featureTypeIssues = keys.features.featureTypeIssues[featureTypeId];
                        keys.features.featureTypeIssues[featureTypeId] = featureTypeIssues.filter(commentIdFilter);
                    }
                }
            });
    }

    deleteFeature(featureId: string, featureTypeId: number) {
        return this.server.deleteFeature(this.contractId, featureId).then(() => {
            const features = this.featureList.features[featureTypeId];
            this.featureList.features[featureTypeId] = features.filter((feature) => {
                return feature.id != featureId;
            });
            if (this.featureList.features[featureTypeId].length === 0) {
                delete this.featureList.features[featureTypeId];
            }
        });
    }

    getPendingAutoLocalizingCount(contractId? : string) {
        let cid = contractId ? contractId : this.contractId;
        return this.server.getPendingAutoLocalizingCount(cid);
    }
}

function mapNvdbFeatureTypes(features: Feature[], datacatalog: datacatalog): { [key: number]: ContractFeatureType } {
    const result: { [key: number]: ContractFeatureType } = {};
    features.forEach(feature => {
        const typeId: number = feature.typeId;
        if (!result[typeId]) {
            const nvdbType = datacatalog.getFeatureType(typeId);
            if (nvdbType) {
                result[typeId] = {
                    id: typeId,
                    name: nvdbType.name,
                    sortNum: nvdbType.sortNum,
                    destination: "NVDB"
                }
            }
        }
    })

    return result;
}
function mapFkbFeatureTypes(features: Feature[], datacatalog: datacatalog): { [key: number]: ContractFeatureType } {
    const result: { [key: number]: ContractFeatureType } = {};
    features.forEach(feature => {
        const typeId: number = feature.typeId;
        if (!result[typeId]) {
            const nvdbType = datacatalog.getFkbFeatureType(typeId);
            result[typeId] = {
                id: typeId,
                name: nvdbType?.name ?? "Ukjent type",
                sortNum: nvdbType?.sortNum ?? 10000,
                destination: "FKB"
            }
        }
    })

    return result;
}

contractService.$inject = [
    '$rootScope',
    '$http',
    '$q',
    '$timeout',
    '$log',
    'server',
    'datacatalog',
    '$routeParams',
    'messages',
    'uibDateParser',
    'UpdateDatacatalogService'];
